import { useCallback, useContext } from "react";
import { CompanyRuralEstablishmentsContext } from "../contexts/CompanyRuralEstablishmentsContext";
import {
  storeRuralEstablishments,
  updateRuralEstablishments,
} from "../../../../../repositories/qerpapi/v3/estabelecimentorural/EstabelecimentoRuralRepository";
import { findByCEP } from "../../../../../repository/services/BigDataService";
import MaskService from "../../../../../utils/mask/maskService";
import { notify } from "../../../../../utils/notify";
import { QueryParamsContext } from "../../../../../context/QueryParamsContext";
import { getCompanyRuralEstablishments } from "../../../../../repositories/qerpapi/v3/screens-repositories/company-rural-establishment";
import { COMPANY_RURAL_API_INTERNAL_CODS } from "../constants";

const perPage = 10;
let typingTimer = null;

export default function useCompanyRuralEstablishmentsController() {
  const { queryParams } = useContext(QueryParamsContext);
  const {
    setMainLoading,
    setRuralEstablishment,
    setRowsTotal,
    textFilter,
    setTextFilter,
    listUf,
    listCounty,
  } = useContext(CompanyRuralEstablishmentsContext);

  const handleGetRuralEstablishments = useCallback(
    async ({ filterValue = "", isPageChange = false, newPage = 1 }) => {
      try {
        if (!queryParams?.empresa_id)
          return notify(
            "Erro ao buscar os estabelecimentos rurais, empresa não encontrada."
          );

        setMainLoading(true);

        const callback = await getCompanyRuralEstablishments({
          page: newPage,
          perPage: perPage,
          filter: {
            companyId: `strict:${queryParams?.empresa_id}`,
          },
          multipleFilterValue: `ilike_unaccent:${filterValue}`,
          relations: "contacts,address",
        });

        if (!callback.success) {
          const internalCode = callback.internalCode;
          let errorMessage =
            "Ocorreu um erro ao buscar os estabelecimentos rurais!";

          if (!!COMPANY_RURAL_API_INTERNAL_CODS[internalCode]) {
            errorMessage = COMPANY_RURAL_API_INTERNAL_CODS[internalCode];
          }

          notify(errorMessage);
        }

        if (isPageChange) return callback.success ? callback : [];

        setRuralEstablishment(callback?.data || []);
        setRowsTotal(callback?.paginate?.total || 0);
      } catch (error) {
        notify("Erro ao buscar os estabelecimentos rurais.");
        console.error("Get Rural Establishments Error: ", error);
      } finally {
        setMainLoading(false);
      }
    },
    [
      queryParams?.empresa_id,
      setMainLoading,
      setRowsTotal,
      setRuralEstablishment,
    ]
  );

  const customHandleChangePage = useCallback(
    async (event, page, itemIndex) => {
      let newPage = page + 1;

      const establishments = await handleGetRuralEstablishments({
        filterValue: textFilter,
        isPageChange: true,
        newPage,
      });

      return establishments?.data || [];
    },
    [handleGetRuralEstablishments, textFilter]
  );

  const handleSearchRuralEstablishments = useCallback(
    (filterValue) => {
      setTextFilter(filterValue);

      clearTimeout(typingTimer);
      typingTimer = setTimeout(async () => {
        handleGetRuralEstablishments({
          filterValue,
          isPageChange: false,
          newPage: 1,
        });
      }, 3000);
    },
    [handleGetRuralEstablishments, setTextFilter]
  );

  const handleCep = useCallback(
    ({
      prop,
      setCep,
      setUf,
      setAddress,
      setDistrict,
      setComplement,
      setCounty,
    }) => {
      if (prop.target.value.length > 10) return;
      const newCep = MaskService.toMask("cep", prop.target.value);

      setCep(newCep);

      const getData = async () => {
        try {
          setMainLoading(true);
          const response = await findByCEP(prop.target.value);
          const responseData = response?.data;

          if (
            response.success &&
            responseData.statusCode === 200 &&
            responseData.data
          ) {
            setUf(
              (current) =>
                listUf.find(
                  (opt) => opt.estado === responseData.data.stateCode
                ) || current
            );
            setAddress(() =>
              (responseData.data.street || "").substring(0, 120)
            );
            setDistrict(() =>
              (responseData.data.neighborhood || "").substring(0, 60)
            );
            setComplement(() =>
              (responseData.data.additionalInfo || "").substring(0, 50)
            );
            setCounty(
              (current) =>
                listCounty.find(
                  (opt) => opt.codigo === responseData.data.ibgeCode
                ) || current
            );
            return;
          }

          notify("CEP não encontrado.");
        } catch (error) {
          notify("CEP não encontrado.");
        } finally {
          setMainLoading(false);
        }
      };

      if (newCep.length === 10) {
        document.activeElement.blur();
        getData();
      }
    },
    [setMainLoading, listUf, listCounty]
  );

  const handleSaveCompanyRuralEstablishment = useCallback(
    async ({ body, establishmentId }) => {
      try {
        setMainLoading(true);

        let callback;

        if (!establishmentId) {
          callback = await storeRuralEstablishments({ body });
        } else {
          callback = await updateRuralEstablishments({ body, establishmentId });
        }

        if (!callback.success) {
          const internalCode = callback.internalCode;
          let errorMessage = `Ocorreu um erro ao ${
            !establishmentId ? "cadastrar" : "editar"
          } o estabelecimento rural!`;

          if (!!COMPANY_RURAL_API_INTERNAL_CODS[internalCode]) {
            errorMessage = COMPANY_RURAL_API_INTERNAL_CODS[internalCode];
          }

          notify(errorMessage);
          return false;
        }

        notify(
          `O estabelecimento rural foi ${
            !establishmentId ? "cadastrado" : "editado"
          } com sucesso!`,
          true,
          "success"
        );

        setTextFilter("");
        return true;
      } catch (error) {
        console.error("Save rural establishments error: ", error);
        notify(
          `Ocorreu um erro ao ${
            !establishmentId ? "cadastrar" : "editar"
          } o estabelecimento rural!`
        );
        return false;
      } finally {
        setMainLoading(false);
      }
    },
    [setMainLoading, setTextFilter]
  );

  return {
    handleGetRuralEstablishments,
    customHandleChangePage,
    handleSearchRuralEstablishments,
    handleCep,
    handleSaveCompanyRuralEstablishment,
  };
}
